import React, { useState, useRef, useEffect } from 'react'
import { Switch } from '@/components/ui/switch'
import { cn } from '@/lib/utils'

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'] as const

// Day-of-week modifiers: Mon-Thu 0%, Fri +10%, Sat +15%, Sun -5%
const DOW_MODIFIERS = [0, 0, 0, 0, 10, 15, -5]
const SEASON_PCT = 20
const BASE_RATE = 100

// Build 14 days starting from a Monday
function buildRates(seasonOn: boolean): number[] {
  return Array.from({ length: 14 }, (_, i) => {
    const dow = i % 7
    let rate = BASE_RATE
    // Apply seasonal modifier first (multiplicative)
    if (seasonOn) {
      rate = rate * (1 + SEASON_PCT / 100)
    }
    // Then day-of-week modifier (multiplicative on top)
    rate = rate * (1 + DOW_MODIFIERS[dow] / 100)
    return Math.round(rate)
  })
}

export default function SeasonCompare() {
  const [seasonOn, setSeasonOn] = useState(false)
  const [changedCells, setChangedCells] = useState<Set<number>>(new Set())
  const prevRates = useRef<number[]>(buildRates(false))

  const rates = buildRates(seasonOn)

  useEffect(() => {
    const changed = new Set<number>()
    rates.forEach((rate, i) => {
      if (rate !== prevRates.current[i]) {
        changed.add(i)
      }
    })
    setChangedCells(changed)
    prevRates.current = rates

    // Clear highlights after animation
    const timer = setTimeout(() => setChangedCells(new Set()), 600)
    return () => clearTimeout(timer)
  }, [seasonOn])

  return (
    <div className="space-y-4">
      {/* Toggle */}
      <div className="flex items-center gap-3">
        <Switch
          checked={seasonOn}
          onCheckedChange={setSeasonOn}
          id="season-toggle"
        />
        <label
          htmlFor="season-toggle"
          className="cursor-pointer select-none text-sm font-medium text-foreground"
        >
          Summer Season{' '}
          <span
            className={cn(
              'inline-block rounded-full px-2 py-0.5 text-xs font-semibold transition-colors duration-200',
              seasonOn
                ? 'bg-emerald-100 text-emerald-700'
                : 'bg-slate-100 text-slate-500'
            )}
          >
            +{SEASON_PCT}%
          </span>
        </label>
      </div>

      {/* Explanation */}
      <p className="text-xs text-muted-foreground">
        Base rate: £{BASE_RATE} &middot; Fri +10% &middot; Sat +15% &middot; Sun -5%
      </p>

      {/* Mini calendar grid */}
      <div className="space-y-1">
        {/* Day headers */}
        <div className="grid grid-cols-7 gap-1">
          {DAY_LABELS.map((day) => (
            <div
              key={day}
              className="text-center text-[10px] font-medium uppercase tracking-wider text-muted-foreground"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Week rows */}
        {[0, 1].map((week) => (
          <div key={week} className="grid grid-cols-7 gap-1">
            {Array.from({ length: 7 }, (_, d) => {
              const idx = week * 7 + d
              const rate = rates[idx]
              const isChanged = changedCells.has(idx)
              const isAboveBase = rate > BASE_RATE
              const dayNum = idx + 1

              return (
                <div
                  key={idx}
                  className={cn(
                    'relative flex flex-col items-center justify-center rounded-lg border py-2 transition-all duration-300',
                    isAboveBase
                      ? 'border-emerald-200/80 bg-emerald-50/60'
                      : 'border-border/60 bg-white',
                    isChanged && 'ring-2 ring-primary/40 ring-offset-1'
                  )}
                  style={{
                    animation: isChanged ? 'calendarPulse 0.6s ease-out' : undefined,
                  }}
                >
                  <span className="text-[10px] text-muted-foreground/60 tabular-nums">
                    {dayNum}
                  </span>
                  <span
                    className={cn(
                      'text-sm font-semibold tabular-nums transition-all duration-300',
                      isAboveBase ? 'text-emerald-700' : 'text-foreground'
                    )}
                  >
                    £{rate}
                  </span>
                </div>
              )
            })}
          </div>
        ))}
      </div>

      {/* Inline keyframes */}
      <style>{`
        @keyframes calendarPulse {
          0% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(13, 148, 136, 0.15); }
          100% { box-shadow: 0 0 0 0 rgba(13, 148, 136, 0); }
        }
      `}</style>
    </div>
  )
}
